// ─── Booking sessions with a coach (member side) ───
// API: booking_options, available_slots, book_session, my_bookings, cancel_booking,
// booking_ics. Times on the wire are ISO UTC; slots are shown in the member's own time
// zone, with the coach's zone noted when it differs. Pure helpers (Intl only, no imports)
// so tests/booking.test.mjs can load this file directly in Node.

export const BOOKING_OPTIONS_CACHE_KEY = "ft-org-booking-options";
export const MY_BOOKINGS_CACHE_KEY = "ft-org-bookings";
export const BOOKING_NOTE_MAX = 500;
export const CANCEL_REASON_MAX = 300;
export const MAX_SLOT_DAYS = 31;

// The member's IANA zone ("Europe/London"), or UTC when the runtime can't tell.
export function memberTimeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
}

const dtf = (tz, opts) => new Intl.DateTimeFormat("en-US", { timeZone: tz || "UTC", ...opts });

// Calendar date ("2026-09-29") of an instant in a zone
export function dateKeyIn(iso, tz) {
  const d = typeof iso === "number" ? new Date(iso) : new Date(iso);
  const p = Object.fromEntries(dtf(tz, { year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d).map(x => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

// "9:30 AM"
export const fmtTime = (iso, tz) => dtf(tz, { hour: "numeric", minute: "2-digit" }).format(new Date(iso));
// "Tue, Sep 29"
export const fmtDate = (iso, tz) => dtf(tz, { weekday: "short", month: "short", day: "numeric" }).format(new Date(iso));
// Short zone name at an instant: "PDT", "GMT+2"
export function zoneName(tz, at = Date.now()) {
  const p = dtf(tz, { timeZoneName: "short" }).formatToParts(new Date(at)).find(x => x.type === "timeZoneName");
  return p ? p.value : tz;
}
// Offset of a zone in minutes at an instant (east positive)
export function zoneOffsetMin(tz, at = Date.now()) {
  const p = Object.fromEntries(dtf(tz, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" })
    .formatToParts(new Date(at)).map(x => [x.type, x.value]));
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return Math.round((asUtc - Math.floor(at / 1000) * 1000) / 60000);
}
// True when the coach's clock differs from the member's at that time (same name or not)
export const zonesDiffer = (memberTz, coachTz, at = Date.now()) => !!coachTz && zoneOffsetMin(memberTz, at) !== zoneOffsetMin(coachTz, at);

// Slots (ISO) grouped by the member's local date: [{ date, slots: [iso] }] in time order
export function slotsByDay(slots, tz) {
  const days = new Map();
  for (const s of [...(Array.isArray(slots) ? slots : [])].sort()) {
    const k = dateKeyIn(s, tz);
    if (!days.has(k)) days.set(k, []);
    days.get(k).push(s);
  }
  return [...days.entries()].map(([date, list]) => ({ date, slots: list }));
}

// A date strip from `fromKey` for `days` days: [{ date, dow: 'Tue', day: 29, month: 'Sep', count }]
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const addDays = (key, n) => { const d = new Date(key + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
export function dateStrip(fromKey, days, grouped) {
  const counts = Object.fromEntries((grouped || []).map(g => [g.date, g.slots.length]));
  return Array.from({ length: Math.max(0, days) }, (_, i) => {
    const date = addDays(fromKey, i);
    const d = new Date(date + "T00:00:00Z");
    return { date, dow: DOW[d.getUTCDay()], day: d.getUTCDate(), month: MON[d.getUTCMonth()], count: counts[date] || 0 };
  });
}

// The window to ask available_slots for: today in the coach's zone, the type's horizon (≤ 31)
export function slotRequest(type, now = Date.now()) {
  const days = Math.max(1, Math.min(MAX_SLOT_DAYS, (parseInt(type && type.max_days_ahead) || 30) + 1));
  return { session_type_id: type && type.id, from: dateKeyIn(now, (type && type.coach_timezone) || "UTC"), days };
}

// "45 min", "1 h", "1 h 30 min"
export function durationLabel(min) {
  const m = Math.max(0, parseInt(min) || 0);
  if (m < 60) return `${m} min`;
  return m % 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m / 60} h`;
}
export const kindLabel = (kind) => (kind === "video" ? "Video call" : "In person");

// "Tue, Sep 29 · 9:30 – 10:15 AM" (or with both meridiems when they differ)
export function whenLabel(b, tz) {
  if (!b || !b.starts_at) return "";
  const start = fmtTime(b.starts_at, tz), end = b.ends_at ? fmtTime(b.ends_at, tz) : "";
  const sm = start.slice(-2), em = end.slice(-2);
  const range = end ? (sm === em ? `${start.slice(0, -3)} – ${end}` : `${start} – ${end}`) : start;
  return `${fmtDate(b.starts_at, tz)} · ${range}`;
}

// Upcoming = still booked and not yet over, soonest first; past = the rest, newest first.
export function splitBookings(list, now = Date.now()) {
  const all = Array.isArray(list) ? list : [];
  const upcoming = all.filter(b => b.status === "booked" && Date.parse(b.ends_at || b.starts_at) > now).sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const past = all.filter(b => !upcoming.includes(b)).sort((a, b) => b.starts_at.localeCompare(a.starts_at));
  return { upcoming, past };
}
export const canCancel = (b, now = Date.now()) => !!b && b.status === "booked" && Date.parse(b.starts_at) > now;
export const statusLabel = (s) => ({ booked: "Booked", cancelled: "Cancelled", completed: "Completed", no_show: "Missed" })[s] || s;

// A safe download name for the calendar file
export function icsFilename(name, startsAt) {
  const base = String(name || "").replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^[-.]+|-+$/g, "");
  const n = base || `session-${String(startsAt || "").slice(0, 10) || "booking"}`;
  return /\.ics$/i.test(n) ? n : `${n}.ics`;
}

export function bookingErrorText(code) {
  switch (code) {
    case "slot_taken": return "That time was just taken. Pick another one.";
    case "not_your_coach": return "You can only book sessions with your own coach.";
    case "not_booked": return "This session is no longer booked.";
    case "not_found": return "That session type is no longer offered.";
    case "rate_limited": return "Too many attempts — try again in a little while.";
    case "network": return "Couldn't reach the server. Check your connection and try again.";
    default: return code ? `Something went wrong (${code}).` : "Something went wrong.";
  }
}
